/**
 * Rider Ratings Page
 * Sprint 4I: View ratings and reviews
 */

import { useEffect, useState } from 'react';
import { Star, Award, Calendar } from 'lucide-react';
import { api } from '@/lib/api';
import { RiderRatingStats, RiderRating, ReliabilityBadge } from '@/lib/constants';
import { formatDistanceToNow } from 'date-fns';

export default function RatingsPage() {
  const [stats, setStats] = useState<RiderRatingStats | null>(null);
  const [ratings, setRatings] = useState<RiderRating[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    loadRatings();
  }, []);
  
  const loadRatings = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch aggregated stats
      const statsResponse = await api.get('/api/ratings/aggregated/rider/me');
      setStats(statsResponse.data.data);
      
      // Fetch recent ratings
      const ratingsResponse = await api.get('/api/ratings/rider/me');
      setRatings(ratingsResponse.data.data || []);
    } catch (err: any) {
      console.error('Failed to load ratings:', err);
      setError(err.message || 'Failed to load ratings');
    } finally {
      setIsLoading(false);
    }
  };
  
  const getBadgeLevel = (score: number): ReliabilityBadge => {
    if (score >= 90) return 'platinum';
    if (score >= 80) return 'gold';
    if (score >= 60) return 'silver';
    return 'bronze';
  };
  
  const badgeConfig = {
    bronze: { label: 'Bronze', color: 'bg-orange-100 text-orange-600 border-orange-300' },
    silver: { label: 'Silver', color: 'bg-gray-100 text-gray-600 border-gray-300' },
    gold: { label: 'Gold', color: 'bg-yellow-100 text-yellow-600 border-yellow-300' },
    platinum: { label: 'Platinum', color: 'bg-blue-100 text-blue-600 border-blue-300' },
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading ratings...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadRatings}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  const badge = stats ? getBadgeLevel(stats.reliability_score) : 'bronze';
  const badgeStyle = badgeConfig[badge];
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <h1 className="text-2xl font-bold">My Ratings</h1>
          <p className="text-sm text-gray-500 mt-1">See what customers think</p>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        {stats && stats.ratings_count > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Average Rating */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {stats.avg_rating.toFixed(1)}
                </div>
                <div className="flex items-center justify-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < Math.round(stats.avg_rating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-sm text-gray-500">Average Rating</p>
              </div>
              
              {/* Total Reviews */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {stats.ratings_count}
                </div>
                <Calendar className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                <p className="text-sm text-gray-500">Total Reviews</p>
              </div>
              
              {/* Reliability Badge */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                <div
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-lg font-semibold mb-2 ${badgeStyle.color}`}
                >
                  <Award className="w-5 h-5" />
                  {badgeStyle.label}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Reliability Score: {stats.reliability_score.toFixed(0)}/100
                </p>
              </div>
            </div>
            
            {/* Rating Distribution */}
            {stats.distribution && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-bold mb-4">Rating Breakdown</h3>
                <div className="space-y-2">
                  {[5, 4, 3, 2, 1].map((starCount) => {
                    const count = stats.distribution?.[starCount] || 0;
                    const percentage = stats.ratings_count > 0 ? (count / stats.ratings_count) * 100 : 0;
                    
                    return (
                      <div key={starCount} className="flex items-center gap-3">
                        <div className="flex items-center gap-1 w-12">
                          <span className="text-sm font-medium">{starCount}</span>
                          <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                        </div>
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-yellow-400 transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600 w-12 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Recent Reviews */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="font-bold mb-4">Recent Reviews</h3>
              {ratings.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No reviews yet</p>
              ) : (
                <div className="space-y-4">
                  {ratings.slice(0, 10).map((rating) => (
                    <div
                      key={rating.id}
                      className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${
                                i < rating.rating_value
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-xs text-gray-500">
                          {formatDistanceToNow(new Date(rating.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      {rating.comment && (
                        <p className="text-sm text-gray-700">{rating.comment}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          /* No Ratings Yet */
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <Star className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-xl font-semibold mb-2">No Ratings Yet</h3>
            <p className="text-gray-500">
              Complete deliveries to start receiving ratings from customers
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

